import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageDropzone from "@/components/ImageDropzone";
import ReceiptModal from "@/components/ReceiptModal";
import { STATUS } from "@/components/AppointmentTable";
import { PrivateImage } from "@/hooks/usePrivateImage";
import { useImageUpload } from "@/hooks/useImageUpload";
import { toast } from "sonner";
import { ArrowLeft, ReceiptText, Trash2, FileImage, User, Stethoscope, CalendarDays, IndianRupee, Paperclip } from "lucide-react";

const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const KINDS = { report: "Lab / Test Report", prescription: "Prescription", scan: "Scan / X-ray", other: "Other" };

const Info = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-3">
    <div className="h-9 w-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
    <div><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div><div className="font-medium text-slate-900" data-testid={testid}>{value}</div></div>
  </div>
);

function AttachmentUploader({ appointmentId, onAdded }) {
  const [kind, setKind] = useState("report");
  const [note, setNote] = useState("");
  const upload = useImageUpload({
    onUploaded: async (data) => {
      try {
        const { data: appt } = await api.post(`/appointments/${appointmentId}/attachments`, { path: data.path, filename: data.filename, kind, note });
        toast.success("Attachment added"); setNote(""); onAdded(appt);
      } catch (e) { toast.error(apiError(e)); }
    },
  });
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="attachment-uploader">
      <h3 className="font-head font-semibold">Add report / image</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Type</Label>
          <Select value={kind} onValueChange={setKind}><SelectTrigger data-testid="attachment-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label>Note (optional)</Label><Input data-testid="attachment-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Blood test 12 Jun" /></div>
      </div>
      <ImageDropzone upload={upload} testid="attachment-upload" label="Click to upload report photo / scan" compact preview={null} />
    </div>
  );
}

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || "admin";
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState("");
  const [receiptOpen, setReceiptOpen] = useState(false);
  const canEdit = can(role, "appointments:update") || can(role, "appointments:clinical");

  const load = useCallback(() => api.get(`/appointments/${id}`).then((r) => setAppt(r.data)).catch((e) => setError(apiError(e, "Appointment not found"))), [id]);
  useEffect(() => { load(); }, [load]);

  const removeAtt = async (attId) => {
    try { const { data } = await api.delete(`/appointments/${id}/attachments/${attId}`); setAppt(data); toast.success("Attachment removed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-slate-500"><div data-testid="patient-detail-error">{error}</div><Button variant="outline" onClick={() => navigate("/dashboard")}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button></div>;
  if (!appt) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  const attachments = appt.attachments || [];
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="patient-detail-page">
      <header className="no-print h-20 bg-white border-b border-slate-200 flex items-center justify-between px-5 sm:px-8 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard"><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="font-head text-xl sm:text-2xl font-bold text-slate-900" data-testid="patient-detail-name">{appt.patient_name}</h1>
            <p className="text-xs text-slate-400">Patient record · <span className="font-mono text-teal-700">{appt.receipt_no}</span></p>
          </div>
        </div>
        <Button onClick={() => setReceiptOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="detail-view-receipt"><ReceiptText className="h-4 w-4 mr-2" /> Receipt</Button>
      </header>

      <div className="max-w-6xl mx-auto p-5 sm:p-8 grid lg:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="space-y-6">
          <section className="bg-white rounded-2xl border border-slate-200 p-6 grid sm:grid-cols-2 gap-5">
            <Info icon={User} label="Patient" value={`${appt.patient_name} · ${appt.patient_age} yrs${appt.phone ? ` · ${appt.phone}` : ""}`} testid="detail-patient" />
            <Info icon={Stethoscope} label="Doctor" value={`${appt.doctor_name} (${appt.doctor_specialty})`} testid="detail-doctor" />
            <Info icon={CalendarDays} label="Visit date" value={fmt(appt.appointment_date)} testid="detail-date" />
            <Info icon={IndianRupee} label="Consultation fee" value={formatINR(appt.amount)} testid="detail-amount" />
            <div className="sm:col-span-2 flex items-center gap-3">
              <Badge variant="outline" className={`capitalize ${STATUS[appt.status] || ""}`} data-testid="detail-status">{appt.status}</Badge>
              <span className="text-xs text-slate-400">Created {fmt(appt.created_at)}</span>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <div><div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Disease / Condition</div><div className="font-medium text-slate-900" data-testid="detail-disease">{appt.disease_name || "—"}</div></div>
            <div><div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Diagnosis & Advice</div><div className="text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 min-h-[60px]" data-testid="detail-diagnosis">{appt.diagnosis || "—"}</div></div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Paperclip className="h-4 w-4 text-teal-600" /> Reports & Images ({attachments.length})</h3>
            {attachments.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No reports attached yet.</div> : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="attachments-grid">
                {attachments.map((a) => (
                  <div key={a.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid="attachment-item">
                    <PrivateImage path={a.path} alt={a.filename} className="h-44 w-full object-cover" />
                    <div className="p-3 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Badge variant="outline" className="bg-slate-50">{KINDS[a.kind] || a.kind}</Badge>
                        <div className="text-sm font-medium text-slate-900 truncate mt-1">{a.filename}</div>
                        {a.note && <div className="text-xs text-slate-500">{a.note}</div>}
                        <div className="text-[11px] text-slate-400">{fmt(a.created_at)} · {a.uploaded_by}</div>
                      </div>
                      {canEdit && <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => removeAtt(a.id)} data-testid="remove-attachment-button"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          {canEdit && <AttachmentUploader appointmentId={appt.id} onAdded={setAppt} />}
        </div>

        <aside className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><FileImage className="h-4 w-4 text-teal-600" /> Prescription</h3>
          {appt.prescription_path
            ? <PrivateImage path={appt.prescription_path} alt="Prescription" className="w-full rounded-xl border" testid="detail-prescription-image" />
            : <div className="text-sm text-slate-400 py-10 text-center" data-testid="detail-no-prescription">No prescription image attached.</div>}
        </aside>
      </div>
      <ReceiptModal open={receiptOpen} onOpenChange={setReceiptOpen} appointment={appt} />
    </div>
  );
}
